import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";
import {
  durableGet,
  durableSet,
  getBinding,
  getScreenId,
} from "../lib/binding";
import { bridge, detectPlatform, deviceModel } from "../lib/foyerBridge";
import { hostParams } from "../lib/hostQuery";
import { tizenKeyClaimReport } from "../lib/tizenKeys";

/**
 * `?probe=1` - the first-light diagnostic surface (TIZEN-2).
 *
 * A panel we have never run on is a pile of estimates until something renders
 * on it. This page turns one photograph into the answer to most of the TIZEN
 * series: what engine is this, which platform globals exist, which CSS
 * features are real, does storage survive a power cut, does the service worker
 * register, what does the remote actually emit.
 *
 * THREE RULES, all learned the expensive way and all load-bearing here:
 *
 *  1. NOTHING MAY THROW. This page runs on hardware whose API surface we are
 *     guessing at. A probe that white-screens because `webapis.productinfo`
 *     was absent has destroyed the very measurement it was written to take.
 *     Every platform read goes through `safe()`.
 *
 *  2. NO MODERN CSS. The page must render on an engine older than the floor we
 *     hope to find - otherwise a Tizen 5 panel shows a blank wall and we learn
 *     nothing at all, when what we needed to learn was "this is Tizen 5". So:
 *     no container queries, no `gap`, no `inset`, no `clamp()`, no
 *     `aspect-ratio`. Margins and absolute pixels only. Do not "modernise"
 *     this file - the restraint IS the feature.
 *
 *  3. NO SECRETS. It reports that a binding EXISTS, never the token. The page
 *     is reachable on the production origin by anyone who guesses the param,
 *     exactly like `?diag=1`, so it may only ever disclose facts about the
 *     device and the browser.
 *
 * Typography is sized to be legible in a photograph taken from across a room,
 * because that is how the results will actually come back from the bench.
 */

/** Run a platform read that may not exist, may throw, or may do both. */
function safe<T>(fn: () => T, fallback = "—"): string {
  try {
    const v = fn();
    if (v === undefined || v === null || v === "") return fallback;
    return typeof v === "string" ? v : String(v);
  } catch (err) {
    return `!! ${err instanceof Error ? err.message : "threw"}`;
  }
}

/** Enumerate the callable/own keys of a platform namespace, safely. */
function keysOf(obj: unknown): string[] {
  try {
    if (!obj || typeof obj !== "object") return [];
    const out = new Set<string>();
    for (const k of Object.keys(obj as object)) out.add(k);
    // Samsung's namespaces are often prototype-backed, so own-keys alone can
    // come back empty on an object that is in fact fully populated.
    const proto = Object.getPrototypeOf(obj as object);
    if (proto && proto !== Object.prototype) {
      for (const k of Object.getOwnPropertyNames(proto)) {
        if (k !== "constructor") out.add(k);
      }
    }
    return Array.from(out).sort();
  } catch {
    return [];
  }
}

/** `true`/`false`/`absent` for a global we are hunting for. */
function presence(path: string): string {
  try {
    const parts = path.split(".");
    let cur: unknown = window as unknown as Record<string, unknown>;
    for (const p of parts) {
      if (cur === undefined || cur === null) return "absent";
      cur = (cur as Record<string, unknown>)[p];
    }
    if (cur === undefined || cur === null) return "absent";
    return typeof cur === "function" ? "fn" : typeof cur;
  } catch {
    return "threw";
  }
}

function cssSupports(prop: string, value: string): boolean {
  try {
    return (
      typeof window.CSS !== "undefined" &&
      typeof window.CSS.supports === "function" &&
      window.CSS.supports(prop, value)
    );
  } catch {
    return false;
  }
}

/**
 * The boot counter is the HARD POWER CUT test (TIZEN-7's core question), and
 * it is deliberately kept in two independent stores: `durableGet/Set` (which
 * on Android goes through the native shell's SharedPreferences) and plain
 * `localStorage`. On a Samsung panel we expect them to be the same thing - but
 * "expect" is what this whole page exists to replace. If they ever disagree,
 * that disagreement is the finding.
 */
const BOOTS_DURABLE = "foyerProbeBootsDurable";
const BOOTS_LOCAL = "foyerProbeBootsLocal";

function bumpCounter(read: () => string | null, write: (v: string) => void) {
  try {
    const n = Number.parseInt(read() ?? "0", 10);
    const next = Number.isFinite(n) ? n + 1 : 1;
    write(String(next));
    return next;
  } catch {
    return -1;
  }
}

interface KeyEcho {
  key: string;
  code: string;
  keyCode: number;
  at: string;
}

/**
 * `live` = the probe is an OVERLAY over a running app (the remote chord), so
 * the device is still heartbeating and still accepting fleet commands. Without
 * it, the probe REPLACED the app (`?probe=1`) and the panel is off the command
 * channel entirely - which is what made "Capture logs" sit at SENT forever with
 * no artifact and no explanation. The distinction is printed rather than
 * assumed, because "is this device online right now?" has opposite answers on
 * the two routes and there is no other way to tell them apart on a photograph.
 */
export default function PlatformProbe({ live = false }: { live?: boolean }) {
  const [keys, setKeys] = useState<KeyEcho[]>([]);
  const [sw, setSw] = useState("checking…");
  const [videoState, setVideoState] = useState("no ?video= url given");
  const bumped = useRef(false);
  const [boots, setBoots] = useState({ durable: 0, local: 0 });
  const [installed, setInstalled] = useState("reading…");
  const [kioskUnlock, setKioskUnlock] = useState<
    "idle" | "requested" | "unsupported"
  >("idle");

  const params = hostParams();
  const videoUrl = params.get("video");

  // Scale the type to the panel. This page is read off a PHOTOGRAPH taken at a
  // TV, so 15px sized for a laptop is a wasted trip to the bench: on a 1920
  // panel it is half the size it should be. Computed in JS from the measured
  // viewport rather than with `clamp()`/`vw`, because rule 2 in the header
  // holds - this must render on an engine older than the floor we hope to
  // find, and `clamp()` is Chromium 79. `overflow: auto` on the root means the
  // worst case is scrolling, never clipped content.
  const baseFont = Math.max(
    15,
    Math.min(24, Math.round((window.innerWidth || 1280) / 88))
  );

  // Count this boot exactly once (StrictMode double-invokes effects in dev).
  useEffect(() => {
    if (bumped.current) return;
    bumped.current = true;
    setBoots({
      durable: bumpCounter(
        () => durableGet(BOOTS_DURABLE),
        (v) => durableSet(BOOTS_DURABLE, v)
      ),
      local: bumpCounter(
        () => window.localStorage.getItem(BOOTS_LOCAL),
        (v) => window.localStorage.setItem(BOOTS_LOCAL, v)
      ),
    });
  }, []);

  // Live remote-key echo. Deliberately does NOT preventDefault: if the Home or
  // Back key takes the screen away from us, that is TIZEN-15's finding and we
  // want to see it happen rather than suppress it.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const now = new Date();
      const p = (n: number) => String(n).padStart(2, "0");
      setKeys((prev) =>
        [
          {
            key: e.key ?? "—",
            code: e.code ?? "—",
            keyCode: e.keyCode,
            at: `${p(now.getHours())}:${p(now.getMinutes())}:${p(now.getSeconds())}`,
          },
          ...prev,
        ].slice(0, 8)
      );
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Service worker: supported / registered / controlling are three different
  // answers and TIZEN-12 needs to know which one we got.
  useEffect(() => {
    try {
      if (!("serviceWorker" in navigator)) {
        setSw("unsupported (no navigator.serviceWorker)");
        return;
      }
      const controlled = navigator.serviceWorker.controller ? "yes" : "no";
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) => {
          setSw(
            `supported · registrations=${regs.length} · controlling=${controlled}`
          );
        })
        .catch((e) => setSw(`supported, getRegistrations threw: ${String(e)}`));
    } catch (e) {
      setSw(`threw: ${String(e)}`);
    }
  }, []);

  // Installed-app inventory. This is the ONLY trustworthy source of Tizen app
  // ids (TIZEN-14): the Nöje screen hides on this panel because every catalogue
  // entry carries an `androidSlug` and no `tizenId`, and the fix is data, not
  // code. Samsung's public ids are widely mis-quoted on the web and a WRONG
  // `tizenId` is worse than a missing one - it renders a tile that dies on
  // press. So we read the ids off the panel that has to launch them.
  //
  // `getAppsInfo` is callback-style (pre-Promise Tizen API) and needs
  // `application.info`, which config.xml already declares.
  useEffect(() => {
    try {
      const app = (
        window as unknown as {
          tizen?: {
            application?: {
              getAppsInfo?: (
                ok: (list: Array<{ id?: string; name?: string }>) => void,
                err: (e: unknown) => void
              ) => void;
            };
          };
        }
      ).tizen?.application;
      if (!app || typeof app.getAppsInfo !== "function") {
        setInstalled("tizen.application.getAppsInfo absent");
        return;
      }
      app.getAppsInfo(
        (list) => {
          try {
            const rows = (Array.isArray(list) ? list : [])
              .map((a) => `${a?.name ?? "?"} = ${a?.id ?? "?"}`)
              .sort();
            setInstalled(
              rows.length ? `${rows.length} installed\n${rows.join("\n")}` : "0"
            );
          } catch (e) {
            setInstalled(`render threw: ${String(e)}`);
          }
        },
        (e) =>
          setInstalled(
            `denied/threw: ${e instanceof Error ? e.message : String(e)}`
          )
      );
    } catch (e) {
      setInstalled(`threw: ${String(e)}`);
    }
  }, []);

  const platform = safe(() => detectPlatform());
  const binding = safe(() => (getBinding() ? "present" : "none"), "none");

  // ---- identity -----------------------------------------------------------
  const identity: Array<[string, string]> = [
    ["detectPlatform()", platform],
    ["deviceModel()", safe(() => deviceModel())],
    ["screenId", safe(() => getScreenId(), "none")],
    ["binding", binding],
    ["app version", safe(() => import.meta.env?.VITE_APP_VERSION ?? "dev")],
    [
      "bundle",
      // If SystemJS is on the page, the nomodule/legacy bundle is what the
      // panel chose - which is exactly what TIZEN-2 asks us to confirm.
      presence("System") !== "absent" ? "LEGACY (SystemJS)" : "modern (ESM)",
    ],
    ["userAgent", safe(() => navigator.userAgent)],
    ["screen", safe(() => `${window.screen.width}×${window.screen.height}`)],
    ["devicePixelRatio", safe(() => window.devicePixelRatio)],
    ["language", safe(() => navigator.language)],
    [
      "platform.version",
      safe(() =>
        (
          window as unknown as {
            tizen?: {
              systeminfo?: {
                getCapability?: (k: string) => unknown;
              };
            };
          }
        ).tizen?.systeminfo?.getCapability?.(
          "http://tizen.org/feature/platform.version"
        )
      ),
    ],
    [
      "web_api.version",
      safe(() =>
        (
          window as unknown as {
            tizen?: {
              systeminfo?: { getCapability?: (k: string) => unknown };
            };
          }
        ).tizen?.systeminfo?.getCapability?.(
          "http://tizen.org/feature/web_api.version"
        )
      ),
    ],
  ];

  // ---- privileged device reads --------------------------------------------
  // PRESENCE IS NOT PERMISSION. The panel showed `webapis.productinfo` as an
  // object while the widget declared only public privileges - and the
  // autoplayer's hardest-won lesson is that Samsung privilege enforcement
  // happens at CALL time, not at install (commit 09b8420: a trimmed
  // config.xml installed perfectly and silently broke DRM, telemetry and OTA).
  //
  // So the only honest test is to CALL them. `safe()` turns a permission
  // refusal into a printed `!! ...` rather than a white screen, which means
  // the failure mode IS the measurement: a value means the certificate
  // carries the privilege, a throw names what is missing.
  //
  // getDuid is the one that matters most - it is TIZEN-7's whole pairing
  // mechanism (the DUID is the serial).
  function productInfo(): Record<
    string,
    ((...a: unknown[]) => unknown) | undefined
  > {
    const w = window as unknown as {
      webapis?: { productinfo?: Record<string, (...a: unknown[]) => unknown> };
    };
    return w.webapis?.productinfo ?? {};
  }
  function callProductInfo(fn: string): string {
    return safe(() => {
      const f = productInfo()[fn];
      if (typeof f !== "function") return "no such method";
      return f();
    });
  }

  const device: Array<[string, string]> = [
    ["getDuid()", callProductInfo("getDuid")],
    ["getRealModel()", callProductInfo("getRealModel")],
    ["getModel()", callProductInfo("getModel")],
    ["getModelCode()", callProductInfo("getModelCode")],
    ["getFirmware()", callProductInfo("getFirmware")],
    ["getVersion()", callProductInfo("getVersion")],
    ["getSmartTVServerVersion()", callProductInfo("getSmartTVServerVersion")],
  ];

  // ---- globals ------------------------------------------------------------
  const globals: Array<[string, string]> = [
    ["window.tizen", presence("tizen")],
    ["tizen.application", presence("tizen.application")],
    ["tizen.systeminfo", presence("tizen.systeminfo")],
    ["tizen.filesystem", presence("tizen.filesystem")],
    ["window.webapis", presence("webapis")],
    ["webapis.productinfo", presence("webapis.productinfo")],
    ["webapis.systemcontrol", presence("webapis.systemcontrol")],
    ["webapis.network", presence("webapis.network")],
    ["webapis.remotepower", presence("webapis.remotepower")],
    ["webapis.avplay", presence("webapis.avplay")],
    ["b2bapis.b2bcontrol", presence("b2bapis.b2bcontrol")],
    ["window.FoyerNative", presence("FoyerNative")],
    // The WGT bootloader loads `$WEBAPIS/webapis/webapis.js`, which is what
    // makes the Samsung namespaces exist at all. If that script 404s, whole
    // namespaces read "absent" and it looks like a privilege problem when it
    // is really a missing file - two very different fixes.
    [
      "webapis.js loaded",
      presence("__WEBAPIS_JS_FAILED__") === "absent"
        ? "yes (or n/a)"
        : "FAILED",
    ],
  ];

  const webapisKeys = keysOf(
    (window as unknown as Record<string, unknown>).webapis
  );
  const productinfoKeys = keysOf(
    (
      (window as unknown as Record<string, unknown>).webapis as
        | Record<string, unknown>
        | undefined
    )?.productinfo
  );
  const systemcontrolKeys = keysOf(
    (
      (window as unknown as Record<string, unknown>).webapis as
        | Record<string, unknown>
        | undefined
    )?.systemcontrol
  );

  // ---- CSS ----------------------------------------------------------------
  // Measured, not assumed. These are the exact features src/tv depends on, in
  // the order they became available in Chromium, so the first `no` names the
  // engine's ceiling.
  const css: Array<[string, boolean, string]> = [
    ["flex gap", cssSupports("gap", "1px"), "Cr84"],
    ["inset", cssSupports("inset", "0px"), "Cr87"],
    ["clamp()", cssSupports("width", "clamp(1px,2px,3px)"), "Cr79"],
    ["aspect-ratio", cssSupports("aspect-ratio", "16/9"), "Cr88"],
    ["container-type", cssSupports("container-type", "size"), "Cr105"],
    ["cqw units", cssSupports("width", "1cqw"), "Cr105"],
    ["object-fit", cssSupports("object-fit", "cover"), "Cr32"],
    ["position:sticky", cssSupports("position", "sticky"), "Cr56"],
    ["CSS vars", cssSupports("--x", "1"), "Cr49"],
  ];

  // ---- video --------------------------------------------------------------
  const canPlay: Array<[string, string]> = [
    [
      "H.264 mp4",
      safe(
        () =>
          document
            .createElement("video")
            .canPlayType('video/mp4; codecs="avc1.42E01E"') || "no"
      ),
    ],
    [
      "HLS",
      safe(
        () =>
          document
            .createElement("video")
            .canPlayType("application/vnd.apple.mpegurl") || "no"
      ),
    ],
    [
      "DASH (MSE)",
      safe(() =>
        typeof window.MediaSource !== "undefined" ? "MSE yes" : "no"
      ),
    ],
    [
      "EME",
      safe(() =>
        typeof navigator.requestMediaKeySystemAccess === "function"
          ? "yes"
          : "no"
      ),
    ],
  ];

  /**
   * The installer's escape hatch onto native Philips Professional Settings
   * (CDB URL, room number, network, ...). `KioskReturnGuard` normally
   * reclaims the screen within ~700ms of Foyer being backgrounded by
   * anything it didn't start itself - which is exactly what the remote's
   * unlock combo (mute-left-right-up-down-ok) triggers, so Professional
   * Settings opened but was immediately buried again. This suspends that
   * guard for several minutes so the combo actually holds. See
   * `KioskReturnGuard.unlockForProSettings` (native) for the auto-relock
   * safety net - nothing needs to be pressed to turn it back off.
   */
  function unlockKiosk() {
    try {
      setKioskUnlock(
        bridge.unlockKioskForProSettings() ? "requested" : "unsupported"
      );
    } catch {
      setKioskUnlock("unsupported");
    }
  }

  return (
    <div style={{ ...S.page, fontSize: baseFont }} data-testid="platform-probe">
      <div style={S.header}>
        <span style={S.title}>FOYER PLATFORM PROBE</span>
        <span style={S.headerRight}>
          {platform.toUpperCase()} &middot; boots d{boots.durable}/l
          {boots.local}
        </span>
      </div>

      {/* Whether this panel is still answering the fleet WHILE you read this.
          On the overlay route the app runs underneath, so a reboot or a log
          capture issued from the CMS right now will be obeyed. On the ?probe=1
          route it will not be, and a command issued against this device will
          sit at "sent" until the probe is closed. */}
      <div style={live ? S.bannerLive : S.bannerDead}>
        {live
          ? "OVERLAY — the app is running underneath: still online, still taking CMS commands. Press Back to close."
          : "STANDALONE (?probe=1) — the app is NOT running: this device is offline to the fleet and will ignore CMS commands until you leave this page."}
      </div>

      <div style={S.section}>
        <div style={S.sectionTitle}>
          KIOSK &mdash; unlock native Professional Settings
        </div>
        <button
          type="button"
          autoFocus
          onClick={unlockKiosk}
          style={S.unlockButton}
          data-testid="unlock-kiosk"
        >
          UNLOCK KIOSK (10 MIN)
        </button>
        <div style={S.hint}>
          {kioskUnlock === "idle" &&
            "Press, then use the remote's Professional Settings combo (mute-left-right-up-down-ok). Foyer steps aside instead of reclaiming the screen. Re-locks on its own - no need to press anything after."}
          {kioskUnlock === "requested" &&
            "Requested. Foyer will not reclaim the screen for the next ~10 minutes, or until it comes back to the foreground on its own - whichever is first."}
          {kioskUnlock === "unsupported" &&
            "Not available on this platform/shell - there is no kiosk-reclaim behaviour here to suspend."}
        </div>
      </div>

      <div style={S.cols}>
        <div style={S.col}>
          <Section title="IDENTITY" rows={identity} />
          <Section title="STORAGE" rows={storageRows(boots)} />
          <Section title="SERVICE WORKER" rows={[["state", sw]]} />
        </div>

        <div style={S.col}>
          <Section title="DEVICE — PRIVILEGED CALLS" rows={device} />
          <Section title="PLATFORM GLOBALS" rows={globals} />
          <Block title="webapis.*" body={webapisKeys.join("  ") || "—"} />
          <Block
            title="webapis.productinfo.*"
            body={productinfoKeys.join("  ") || "—"}
          />
          <Block
            title="webapis.systemcontrol.*"
            body={systemcontrolKeys.join("  ") || "—"}
          />
          {/* One line per installed app, "Name = id". This is the harvest that
              fills `tizenId` in the app catalogue - photograph it. */}
          <div style={S.section}>
            <div style={S.sectionTitle}>
              INSTALLED APPS &mdash; tizenId harvest
            </div>
            <div style={S.blockPre}>{installed}</div>
          </div>
        </div>

        <div style={S.col}>
          <div style={S.section}>
            <div style={S.sectionTitle}>CSS SUPPORT</div>
            {css.map(([label, ok, since]) => (
              <div key={label} style={S.row}>
                <span style={S.k}>{label}</span>
                <span style={ok ? S.vYes : S.vNo}>
                  {ok ? "yes" : "NO"} <span style={S.since}>{since}</span>
                </span>
              </div>
            ))}
          </div>
          <Section title="VIDEO" rows={canPlay} />
          <Section title="PLAYBACK" rows={[["<video src>", videoState]]} />

          <div style={S.section}>
            <div style={S.sectionTitle}>REMOTE KEYS &mdash; press anything</div>
            {/* Which keys the firmware actually handed us. `Exit` is the one
                that decides TIZEN-15: unclaimed, the panel closes the app out
                from under the guest no matter what the app does. */}
            <div style={S.row}>
              <span style={S.k}>registerKey</span>
              <span style={S.v}>{safe(() => tizenKeyClaimReport())}</span>
            </div>
            {keys.length === 0 ? (
              <div style={S.hint}>waiting for a key…</div>
            ) : (
              keys.map((k, i) => (
                <div key={`${k.at}-${i}`} style={S.row}>
                  <span style={S.k}>
                    {k.at} key=<b>{k.key}</b>
                  </span>
                  <span style={S.v}>
                    keyCode=<b>{k.keyCode}</b> code={k.code}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {videoUrl ? (
        <video
          src={videoUrl}
          autoPlay
          muted
          loop
          playsInline
          style={S.video}
          onPlaying={() => setVideoState("playing")}
          onWaiting={() => setVideoState("waiting (stall)")}
          onError={(e) =>
            setVideoState(
              `error code=${
                (e.currentTarget as HTMLVideoElement).error?.code ?? "?"
              }`
            )
          }
          onLoadedMetadata={(e) =>
            setVideoState(
              `metadata ${(e.currentTarget as HTMLVideoElement).videoWidth}×${
                (e.currentTarget as HTMLVideoElement).videoHeight
              }`
            )
          }
        />
      ) : null}

      <div style={S.footer}>
        ?probe=1 &nbsp;|&nbsp; add &amp;video=&lt;url&gt; to test playback
        &nbsp;|&nbsp; pull the power and reload: the boot counters must keep
        climbing
      </div>
    </div>
  );
}

function storageRows(boots: {
  durable: number;
  local: number;
}): Array<[string, string]> {
  const roundTrip = safe(() => {
    const probe = "__foyer_probe__";
    window.localStorage.setItem(probe, "1");
    const ok = window.localStorage.getItem(probe) === "1";
    window.localStorage.removeItem(probe);
    return ok ? "read-back OK" : "WROTE BUT DID NOT READ BACK";
  });
  return [
    ["localStorage", roundTrip],
    ["boots (durable)", String(boots.durable)],
    ["boots (localStorage)", String(boots.local)],
    ["quota-ish", safe(() => `${window.localStorage.length} keys`)],
  ];
}

/**
 * A long value floated right wraps mid-word against the key and becomes
 * unreadable in a photograph (`controlling=n` / `o`), which is the one thing
 * this page may not be. Past ~26 characters the value gets its own full-width
 * line instead.
 */
const VALUE_WRAPS_AT = 26;

function Section({
  title,
  rows,
}: {
  title: string;
  rows: Array<[string, string]>;
}) {
  return (
    <div style={S.section}>
      <div style={S.sectionTitle}>{title}</div>
      {rows.map(([k, v]) =>
        v.length > VALUE_WRAPS_AT ? (
          <div key={k} style={S.rowStacked}>
            <div style={S.kStacked}>{k}</div>
            <div style={S.vStacked}>{v}</div>
          </div>
        ) : (
          <div key={k} style={S.row}>
            <span style={S.k}>{k}</span>
            <span style={S.v}>{v}</span>
          </div>
        )
      )}
    </div>
  );
}

function Block({ title, body }: { title: string; body: string }) {
  return (
    <div style={S.section}>
      <div style={S.sectionTitle}>{title}</div>
      <div style={S.block}>{body}</div>
    </div>
  );
}

/**
 * Inline styles on purpose: this page must not depend on styles.css, which is
 * the very stylesheet whose modern features we are here to test. Pixels and
 * margins only - see rule 2 in the file header.
 */
const S: Record<string, CSSProperties> = {
  page: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    overflow: "auto",
    background: "#0b0e14",
    color: "#dfe6f2",
    fontFamily: "monospace",
    fontSize: 15, // overridden per-viewport by `baseFont`; em sizes below follow it
    lineHeight: 1.45,
    padding: 14,
    boxSizing: "border-box",
  },
  header: {
    borderBottom: "2px solid #2b3450",
    paddingBottom: 6,
    marginBottom: 10,
    overflow: "hidden",
  },
  title: {
    fontSize: "1.45em",
    fontWeight: "bold",
    color: "#7fd4ff",
    float: "left",
  },
  headerRight: { fontSize: "1.2em", color: "#ffd479", float: "right" },
  cols: { overflow: "hidden" },
  col: {
    width: "33.2%",
    float: "left",
    paddingRight: 10,
    boxSizing: "border-box",
  },
  section: {
    border: "1px solid #232c44",
    borderRadius: 4,
    padding: "6px 8px",
    marginBottom: 8,
  },
  sectionTitle: {
    color: "#7fd4ff",
    fontWeight: "bold",
    fontSize: "0.92em",
    letterSpacing: 1,
    marginBottom: 4,
    borderBottom: "1px solid #232c44",
    paddingBottom: 2,
  },
  row: { overflow: "hidden", borderBottom: "1px dotted #1b2236" },
  rowStacked: { borderBottom: "1px dotted #1b2236", paddingBottom: 2 },
  kStacked: { color: "#8b98b4" },
  vStacked: {
    color: "#dfe6f2",
    wordBreak: "break-all",
    paddingLeft: 10,
  },
  k: { color: "#8b98b4", float: "left", marginRight: 8 },
  v: {
    color: "#dfe6f2",
    float: "right",
    textAlign: "right",
    wordBreak: "break-all",
  },
  vYes: { color: "#7ee787", float: "right", fontWeight: "bold" },
  vNo: { color: "#ff7b72", float: "right", fontWeight: "bold" },
  since: { color: "#5a6788", fontSize: "0.8em" },
  // The two banners. Plain borders and padding only - rule 2 in the header.
  bannerLive: {
    background: "#0f2a17",
    border: "1px solid #2f7d4f",
    color: "#8fe0ac",
    padding: "6px 10px",
    marginBottom: 8,
    fontSize: "0.86em",
  },
  bannerDead: {
    background: "#2a1414",
    border: "1px solid #7d3030",
    color: "#f0a0a0",
    padding: "6px 10px",
    marginBottom: 8,
    fontSize: "0.86em",
  },
  block: { color: "#c9d4e8", fontSize: "0.86em", wordBreak: "break-all" },
  // Same as `block`, but a list where one item per line is the whole point.
  // `pre-wrap` is CSS 2.1 - safe under rule 2.
  blockPre: {
    color: "#c9d4e8",
    fontSize: "0.86em",
    wordBreak: "break-all",
    whiteSpace: "pre-wrap",
  },
  hint: { color: "#5f6b85", fontStyle: "italic" },
  unlockButton: {
    display: "block",
    width: "100%",
    boxSizing: "border-box",
    background: "#1c2942",
    border: "2px solid #7fd4ff",
    color: "#7fd4ff",
    fontFamily: "monospace",
    fontWeight: "bold",
    fontSize: "1.1em",
    letterSpacing: 1,
    padding: "10px 8px",
    marginBottom: 6,
    cursor: "pointer",
  },
  video: {
    position: "absolute",
    right: 14,
    bottom: 40,
    width: 240,
    height: 135,
    background: "#000",
    border: "1px solid #2b3450",
  },
  footer: {
    clear: "both",
    marginTop: 6,
    paddingTop: 6,
    borderTop: "1px solid #232c44",
    color: "#5f6b85",
    fontSize: "0.86em",
  },
};
